import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';

import IOfferProductVersionPost from 'api/interfaces/offers/IOfferProductVersionPost';
import apiConfigurations, {
  IOwnConfiguration,
} from 'api/productConfigurationsFromPosition';
import translations from 'services/translations/translations';
import { addMessage } from 'store/messages/actions';

import 'style/layout/modal/ModalConfirm.css';

/**
 * Saves the alternative open in the calculation window as a product configuration: its
 * materials with their details, installations, other expenses and offer text. A new
 * configuration, or over one the user has made.
 */
const SaveAsConfiguration = ({
  defaultName,
  isOpen,
  toggle,
  version,
}: {
  defaultName: string;
  isOpen: boolean;
  toggle: () => void;
  // The alternative in the format of the position save, read when saving
  version: () => IOfferProductVersionPost;
}): JSX.Element => {
  const dispatch = useDispatch();
  const { t } = useTranslation([translations.calculationWindow]);

  const [own, setOwn] = useState<IOwnConfiguration[]>([]);
  const [overwrite, setOverwrite] = useState(false);
  const [configurationId, setConfigurationId] = useState<string>('');
  const [name, setName] = useState(defaultName);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setName(defaultName);
    setOverwrite(false);
    setConfigurationId('');
    apiConfigurations
      .getOwn()
      .then(setOwn)
      .catch(() => setOwn([]));
  }, [isOpen]);

  const canSave = overwrite ? configurationId !== '' : name.trim() !== '';

  const save = async () => {
    if (!canSave || saving) {
      return;
    }

    setSaving(true);
    try {
      await apiConfigurations.post({
        configurationId: overwrite ? parseInt(configurationId, 10) : null,
        name: name.trim(),
        version: version(),
      });
      dispatch(
        addMessage(
          t(`${translations.calculationWindow}:saveAsConfiguration.Saved`)
        )
      );
      toggle();
    } catch (error) {
      dispatch(
        addMessage(
          t(
            `${translations.calculationWindow}:saveAsConfiguration.${
              error?.response?.status === 403 ? 'Not allowed' : 'Save failed'
            }`
          )
        )
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader className="modal-header" toggle={toggle}>
        {t(`${translations.calculationWindow}:saveAsConfiguration.Title`)}
      </ModalHeader>
      <ModalBody>
        <p>{t(`${translations.calculationWindow}:saveAsConfiguration.Info`)}</p>
        <FormGroup check>
          <Label check>
            <Input
              checked={!overwrite}
              name="save-as-configuration-target"
              onChange={() => setOverwrite(false)}
              type="radio"
            />{' '}
            {t(`${translations.calculationWindow}:saveAsConfiguration.New`)}
          </Label>
        </FormGroup>
        {overwrite ? null : (
          <FormGroup className="mt-2">
            <Label for="save-as-configuration-name">
              {t(`${translations.calculationWindow}:saveAsConfiguration.Name`)}
            </Label>
            {/* The name is typed first, so the keys never go to the save button */}
            <Input
              autoFocus
              id="save-as-configuration-name"
              onChange={(event) => setName(event.target.value)}
              value={name}
            />
          </FormGroup>
        )}
        {own.length === 0 ? null : (
          <>
            <FormGroup check className="mt-2">
              <Label check>
                <Input
                  checked={overwrite}
                  name="save-as-configuration-target"
                  onChange={() => setOverwrite(true)}
                  type="radio"
                />{' '}
                {t(
                  `${translations.calculationWindow}:saveAsConfiguration.Overwrite`
                )}
              </Label>
            </FormGroup>
            {overwrite ? (
              <FormGroup className="mt-2">
                <Input
                  aria-label={t(
                    `${translations.calculationWindow}:saveAsConfiguration.Overwrite`
                  )}
                  onChange={(event) => setConfigurationId(event.target.value)}
                  type="select"
                  value={configurationId}
                >
                  <option value="">
                    {t(
                      `${translations.calculationWindow}:saveAsConfiguration.Choose`
                    )}
                  </option>
                  {own.map((configuration) => (
                    <option key={configuration.id} value={configuration.id}>
                      {configuration.name}
                    </option>
                  ))}
                </Input>
                <p className="mt-2 mb-0">
                  {t(
                    `${translations.calculationWindow}:saveAsConfiguration.Overwrite info`
                  )}
                </p>
              </FormGroup>
            ) : null}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          className="modal-button"
          disabled={!canSave || saving}
          onClick={save}
        >
          {t(`${translations.calculationWindow}:saveAsConfiguration.Save`)}
        </Button>
        <Button className="modal-button" onClick={toggle}>
          {t(`${translations.calculationWindow}:saveAsConfiguration.Cancel`)}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default SaveAsConfiguration;
